import React from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { User, LogOut, Home, Globe, Settings, ChevronDown, BarChart3 } from "lucide-react";

const Navbar: React.FC = () => {
  const { props } = usePage<any>();
  const user = props.auth?.user;

  const handleLogout = () => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/users/sign_out";

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    if (csrfToken) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "authenticity_token";
      input.value = csrfToken;
      form.appendChild(input);
    }

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "delete";
    form.appendChild(methodInput);

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link href="/" className="text-2xl font-semibold tracking-tight">
              Resolute.
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <Link
              href="/dashboard"
              className="flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary"
            >
              <Home className="h-4 w-4" />
              <span>Dashboard</span>
            </Link>

            <Link
              href="/analytics"
              className="flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary"
            >
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </Link>

            <Link
              href="/domains"
              className="flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary"
            >
              <Globe className="h-4 w-4" />
              <span>Domains</span>
            </Link>

            {user?.admin && (
              <Link
                href="/admin/seed_emails"
                className="flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary"
              >
                <Settings className="h-4 w-4" />
                <span>Seed Emails</span>
              </Link>
            )}
          </div>

          <div className="flex items-center">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center space-x-0"
                  >
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline-block">{user.email}</span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="md:hidden" asChild>
                    <Link href="/dashboard" className="w-full">
                      <Home className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="md:hidden" asChild>
                    <Link href="/analytics" className="w-full">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Analytics
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="md:hidden" asChild>
                    <Link href="/domains" className="w-full">
                      <Globe className="mr-2 h-4 w-4" />
                      Domains
                    </Link>
                  </DropdownMenuItem>
                  {user.admin && (
                    <DropdownMenuItem className="md:hidden" asChild>
                      <Link href="/admin/seed_emails" className="w-full">
                        <Settings className="mr-2 h-4 w-4" />
                        Seed Emails
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="md:hidden" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="space-x-4">
                <Link href="/users/sign_in">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
                <Link href="/users/sign_up">
                  <Button size="sm">Sign Up</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
